import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { API, apiRequest } from '../services/api';
import {
  refreshDashboardCache,
  clearDashboardCache,
} from '../services/CacheService';

const AuthContext = createContext();

// Helper to get a cookie value by name
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Token expiry management (1 hour default)
const TOKEN_EXP_KEY = 'token_exp';
const ONE_HOUR_MS = 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const logoutTimerRef = useRef(null);

  const fetchAndSetUserInfo = async (tok) => {
    if (!tok) return;
    try {
      const res = await apiRequest(API.USERS_ME, { token: tok });
      const data = await res.json();
      setUserName(data?.name || null);
      setUserEmail(data?.email || null);
    } catch (e) {
      console.error('AuthContext: Failed to fetch user info', e);
      setUserName(null);
      setUserEmail(null);
    }
  };

  // Schedule automatic logout at a given expiry timestamp (ms since epoch)
  const scheduleAutoLogout = (expiryAtMs) => {
    const msLeft = Number(expiryAtMs) - Date.now();
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (!Number.isFinite(msLeft)) return;
    if (msLeft <= 0) {
      logout();
      return;
    }
    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, msLeft);
  };

  useEffect(() => {
    let storedToken = localStorage.getItem('token');
    // console.log('AuthContext: Token from localStorage:', storedToken);
    if (!storedToken) {
      storedToken = getCookie('token');
      console.log('AuthContext: Token from cookie:', storedToken);
    }
    if (storedToken) {
      setToken(storedToken);
      let decoded = null;
      try {
        decoded = jwtDecode(storedToken);
        // console.log('AuthContext: Decoded token (useEffect):', decoded);
        setUserRole(decoded.role);
        console.log('AuthContext: Set user role to (useEffect):', decoded.role);
      } catch (error) {
        console.error(
          'AuthContext: Failed to decode token (useEffect):',
          error
        );
        setUserRole(null);
      }

      // Determine expiry timestamp (ms) from localStorage, JWT exp, or default 1h from now
      const storedExp = Number(localStorage.getItem(TOKEN_EXP_KEY));
      const jwtExpMs = decoded?.exp ? decoded.exp * 1000 : null;
      let effectiveExp = storedExp || jwtExpMs || Date.now() + ONE_HOUR_MS;

      // If token already expired, perform immediate logout
      if (effectiveExp <= Date.now()) {
        logout();
        setIsLoading(false);
        return;
      }

      // Persist the chosen expiry (so subsequent loads have a single source of truth)
      localStorage.setItem(TOKEN_EXP_KEY, String(effectiveExp));

      // Fetch profile for name/email
      fetchAndSetUserInfo(storedToken);

      // Refresh dashboard cache at startup to ensure accuracy
      refreshDashboardCache(storedToken).catch(() => {});

      // Schedule auto logout
      scheduleAutoLogout(effectiveExp);
    } else {
      console.log('AuthContext: No token found (useEffect).');
      // No token at boot, ensure cache is clean
      clearDashboardCache();
    }
    setIsLoading(false);

    // Cleanup on unmount
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
  }, []);

  // New login function for immediate token processing
  const login = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken); // Ensure token is saved
    let decoded = null;
    try {
      decoded = jwtDecode(newToken);
      setUserRole(decoded.role); // Set userRole immediately
      // console.log('AuthContext: Decoded token (login function):', decoded);
      console.log(
        'AuthContext: Set user role to (login function):',
        decoded.role
      );
    } catch (error) {
      console.error(
        'AuthContext: Failed to decode token (login function):',
        error
      );
      setUserRole(null);
    }

    // Compute expiry: prefer JWT exp, else 1 hour from now
    const expiryAtMs = decoded?.exp
      ? decoded.exp * 1000
      : Date.now() + ONE_HOUR_MS;
    localStorage.setItem(TOKEN_EXP_KEY, String(expiryAtMs));

    // Fetch profile for name/email after login
    fetchAndSetUserInfo(newToken);
    setIsLoading(false); // Ensure loading state is updated

    // Schedule auto logout
    scheduleAutoLogout(expiryAtMs);

    // Refresh caches post-login to keep data accurate
    refreshDashboardCache(newToken).catch(() => {});
  };

  const logout = () => {
    setToken(null);
    setUserRole(null);
    setUserName(null);
    setUserEmail(null);
    localStorage.removeItem('token');
    localStorage.removeItem(TOKEN_EXP_KEY);
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    // Clear caches on logout so future views don't show stale data
    clearDashboardCache();
  };

  // Detect token removal/changes from other tabs and on focus in the same tab
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token removed in another tab
          logout();
        }
      }
    };

    const onVisibilityOrFocus = () => {
      // Same-tab sanity check when user returns focus or tab becomes visible
      const lsToken = localStorage.getItem('token');
      if (!lsToken && token) {
        logout();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [token, logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        userRole,
        userName,
        userEmail,
        logout,
        isLoading,
        login,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
